import { supabase } from '@/lib/supabase';
import { isCurrency, type Currency } from '@/shared/currencies';
import type { Sheet } from './fxRate';
import { setFxRateProvider } from '@/shared/formula/language';

/**
 * Daily exchange-rate history, read from the Numtable library's Exchange rates
 * document (`/api/library/exchange-rates`, see worker/routes/libraryExchangeRates.ts).
 * It backs the grid's "exchange rate" number type: a column that fills itself
 * with the rate of a currency pair on the row's date.
 */

export { FX_MARKETS, FX_SIDES, fxHistoryFromSheets, fxRateOn, type FxHistory, type FxMarket, type FxSide } from './fxRate';
import { fxHistoryFromSheets, fxRateOn, type FxHistory, type FxMarket, type FxSide } from './fxRate';

/** Currencies the library has a daily series for. */
export const FX_HISTORY_CURRENCIES: readonly Currency[] = ['USD', 'EUR', 'ARS', 'BRL', 'CLP', 'COP', 'MXN', 'UYU'];

let loaded: FxHistory | null = null;
const listeners = new Set<() => void>();

/** Calls `listener` each time the history (re)loads; returns the unsubscribe. */
export function onFxHistoryLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

let cache: { at: number; promise: Promise<FxHistory> } | null = null;
const TTL = 10 * 60 * 1000;

async function bearer(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('fx_history_signed_out');
  return token;
}

/** Shared by every grid on the page; refetched after ten minutes. `fresh` rebuilds it at the source. */
export function loadFxHistory(fresh = false): Promise<FxHistory> {
  if (!fresh && cache && Date.now() - cache.at < TTL) return cache.promise;
  const promise = (async () => {
    const response = await fetch(`/api/library/exchange-rates${fresh ? '?fresh=1' : ''}`, { headers: { Authorization: `Bearer ${await bearer()}` } });
    if (!response.ok) throw new Error(`fx_history_${response.status}`);
    const history = fxHistoryFromSheets(((await response.json()) as { sheets?: Sheet[] }).sheets ?? []);
    loaded = history;
    for (const listener of listeners) listener();
    return history;
  })();
  cache = { at: Date.now(), promise };
  promise.catch(() => { cache = null; });
  return promise;
}

// FXRATE reads the loaded history; the first call starts the load and shows #N/A
// until it arrives (grids recompute on `onFxHistoryLoaded`).
setFxRateProvider((pair, day) => {
  if (!loaded || (cache && Date.now() - cache.at >= TTL)) void loadFxHistory().catch((error: unknown) => console.error('FXRATE: exchange-rate history unavailable', error));
  if (!loaded) return undefined;
  if (!isCurrency(pair.from) || !isCurrency(pair.to)) return null;
  return fxRateOn(loaded, { from: pair.from, to: pair.to, market: pair.market as FxMarket, side: pair.side as FxSide }, day);
});

/**
 * Refreshes a spreadsheet's exchange-rate columns on the server (any reader may
 * ask; see worker/routes/libraryExchangeRates.ts). Returns the cells written.
 */
export async function refreshSheetExchangeRates(documentId: string, tabId: string, fresh = false): Promise<{ tabId: string; rowId: string; field: string; value: number }[]> {
  const response = await fetch(`/api/library/exchange-rates/sheets/${documentId}/refresh`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${await bearer()}`, 'content-type': 'application/json' },
    body: JSON.stringify({ tabId, fresh }),
  });
  if (!response.ok) throw new Error(`fx_refresh_${response.status}`);
  return ((await response.json()) as { changes?: { tabId: string; rowId: string; field: string; value: number }[] }).changes ?? [];
}
